import type { Character } from "../types/entity.js";
import type { CheckResult } from "../combat/skillCheck.js";
import { rollSkillCheck } from "../combat/skillCheck.js";
import type { LootTable, LootTableLookup, ResolvedLoot } from "./../inventory/lootTable.js";
import { resolveLootTable } from "../inventory/lootTable.js";
import { randomInt } from "../combat/dice.js";

type IntRng = (min: number, max: number) => number;

/**
 * Pickpocket resolver (N08).
 *
 * Called when the party is sneaking and the player targets a neutral
 * NPC with a pickpocket action. Resolves the DEX (Sleight of Hand)
 * check against `npcPickpocketDC`. On success, draws one loot roll
 * from `npcPickpocketTable`; on failure, the caller is expected to
 * invoke `CombatManager.setHostile(npcInstanceId)` — we just tell
 * them *that* it failed; hostile side-effects stay outside this
 * module so it is easy to unit-test.
 *
 * Pure aside from the dice roll, which accepts an `IntRng` for
 * seeded tests.
 */

export type PickpocketOutcome = "success" | "caught" | "not_pickpocketable";

export interface PickpocketInput {
  readonly rogue: Character;
  readonly npcInstanceId: string;
  readonly npcPickpocketDC?: number | undefined;
  readonly npcPickpocketTable?: string | undefined;
  /** Registry lookup for the NPC's pickpocket loot table. */
  readonly lookupTable?: LootTableLookup;
  readonly rng?: IntRng;
}

export interface PickpocketResult {
  readonly outcome: PickpocketOutcome;
  readonly npcInstanceId: string;
  readonly check: CheckResult | null;
  /** Drops awarded to the rogue on success; empty otherwise. */
  readonly drops: readonly ResolvedLoot[];
}

const NOT_PICKPOCKETABLE: Omit<PickpocketResult, "npcInstanceId"> = {
  outcome: "not_pickpocketable",
  check: null,
  drops: [],
};

/**
 * Run a pickpocket attempt. The caller is responsible for:
 *   - sneak-gate (refuse if party not sneaking)
 *   - selecting the rogue (active-slot / high-DEX character)
 *   - on `caught`: calling CombatManager.setHostile(npcInstanceId)
 *   - on `success`: adding `drops` to the rogue's inventory
 */
export function attemptPickpocket(input: PickpocketInput): PickpocketResult {
  const { rogue, npcInstanceId, npcPickpocketDC, npcPickpocketTable, lookupTable, rng } = input;

  if (npcPickpocketDC === undefined) {
    return { ...NOT_PICKPOCKETABLE, npcInstanceId };
  }

  const check = rollSkillCheck(rogue, "Sleight of Hand", npcPickpocketDC);

  if (!check.success) {
    return { outcome: "caught", npcInstanceId, check, drops: [] };
  }

  if (!npcPickpocketTable || !lookupTable) {
    return { outcome: "success", npcInstanceId, check, drops: [] };
  }

  const table: LootTable | undefined = lookupTable(npcPickpocketTable);
  if (!table) {
    return { outcome: "success", npcInstanceId, check, drops: [] };
  }

  const drops = resolveLootTable(table, lookupTable, rng ?? randomInt);
  // Spec: *one* random item from the table. Pick one from the resolved
  // list so weighted tables still honor their chance distribution.
  if (drops.length === 0) {
    return { outcome: "success", npcInstanceId, check, drops: [] };
  }
  const choiceRng = rng ?? randomInt;
  const pickIndex = choiceRng(0, drops.length - 1);
  const chosen = drops[pickIndex]!;
  return {
    outcome: "success",
    npcInstanceId,
    check,
    drops: [chosen],
  };
}
